import {
  IconAngle,
  IconBattery,
  IconClock,
  IconTemperature,
} from "@tabler/icons-react"
import { Card, CardContent } from "@/components/ui/card"
import { MetricTile } from "@/components/ui/metric-tile"
import { Sparkline, type SparklinePoint } from "@/components/ui/sparkline"
import { useDeviceStatus } from "@/hooks/use-device-status"
import {
  formatBatteryVoltage,
  formatSpecificGravity,
  formatTemperature,
  formatTiltAngle,
} from "@/lib/ble/parsers"
import type { DeviceSettings, SensorReading } from "@/types/device"

interface ReadingsPanelMobileProps {
  deviceId: number
  reading: SensorReading
  settings: DeviceSettings | null
  lastSeenAt: string | null
}

const ONE_SECOND_MS = 1000
const ONE_MINUTE_MS = 60 * ONE_SECOND_MS
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS
const ONE_DAY_MS = 24 * ONE_HOUR_MS

function formatLastSeen(lastSeenAt: string | null, _tick: number): string {
  if (!lastSeenAt) return "Never"
  const diffMs = Date.now() - Date.parse(lastSeenAt)
  if (diffMs < ONE_MINUTE_MS) {
    const seconds = Math.max(1, Math.floor(diffMs / ONE_SECOND_MS))
    return seconds <= 1 ? "Just now" : `${seconds}s ago`
  }
  if (diffMs < ONE_HOUR_MS) return `${Math.floor(diffMs / ONE_MINUTE_MS)}m ago`
  if (diffMs < ONE_DAY_MS) return `${Math.floor(diffMs / ONE_HOUR_MS)}h ago`
  return `${Math.floor(diffMs / ONE_DAY_MS)}d ago`
}

export function ReadingsPanelMobile({
  deviceId,
  reading,
  settings,
  lastSeenAt,
}: ReadingsPanelMobileProps) {
  const tempUnit = settings?.temp_unit ?? "fahrenheit"
  const { getReadingsHistory, getLastSeenAt, tick } = useDeviceStatus()

  const history = getReadingsHistory(deviceId)
  const sparkData: SparklinePoint[] = history.map((point) => ({ t: point.t, v: point.sg }))
  const effectiveLastSeen = getLastSeenAt(deviceId) ?? lastSeenAt

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div>
          <span className="text-xs text-muted-foreground">Specific Gravity</span>
          <div className="text-5xl font-medium tabular-nums leading-none">
            {formatSpecificGravity(reading.specificGravity)}
          </div>
          {reading.specificGravity !== null && (
            <span className="mt-1 inline-block text-xs text-muted-foreground">
              {((reading.specificGravity - 1) * 1000).toFixed(1)} points above water
            </span>
          )}
        </div>

        <Sparkline
          data={sparkData}
          ariaLabel="Specific gravity over the last 24 hours"
        />

        <div className="grid grid-cols-2 gap-2">
          <MetricTile
            icon={<IconTemperature className="size-4 text-orange-500" />}
            label="Temp"
            value={formatTemperature(reading.temperature, tempUnit)}
          />
          <MetricTile
            icon={<IconAngle className="size-4 text-purple-500" />}
            label="Tilt"
            value={formatTiltAngle(reading.tiltAngle)}
          />
          <MetricTile
            icon={<IconBattery className="size-4 text-green-500" />}
            label="Battery"
            value={formatBatteryVoltage(reading.batteryVoltage)}
          />
          <MetricTile
            icon={<IconClock className="size-4 text-muted-foreground" />}
            label="Last seen"
            value={formatLastSeen(effectiveLastSeen, tick)}
          />
        </div>
      </CardContent>
    </Card>
  )
}
